import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../../hook/useAppSelector';
import FallbackLoader from '../../ContentWrapper/Content/Loader/FallbackLoader';
import Table from '../../ContentWrapper/Common/Table/Table';
import { COLUMN_MAP } from '../../../constant/constants';
import wallet from '../../../assets/img/svg/table/wallet.svg';
import search_file from '../../../assets/img/svg/table/search-file.svg';
import { t } from 'i18next';

import {
  resetCashRegister,
  setSelectedCCY,
  setSelectedItem,
  setSelectedLocation,
} from '../../../store/slice/cash-register-slice';
import { useAppDispatch } from '../../../hook/useAppDispatch';
import CashRegisterDetails from './CashRegisterDetails';
import OperatorDetails from './OperatorDetails';
import { useFetchDetails } from '../../../hook/useFetchDetails';
import { setColumns } from '../../../store/slice/column-slice';
import { setShowColumnOrder } from '../../../store/slice/global-filters';

const CashRegisterPage = () => {
  const dispatch = useAppDispatch();

  const cashRegisterData = useAppSelector((state) => state.cashRegisterData);
  const loading = useAppSelector((state) => state.cashRegisterData.loading);
  const selectedItem = useAppSelector((state) => state.cashRegisterData.selectedItem);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const operatorModalOpen = useAppSelector((state) => state.operatorDetails.operatorModalOpen);
  const selectedOperator = useAppSelector((state) => state.operatorDetails.selectedOperator);

  const columns = useMemo(() => {
    if (!cashRegisterData.data?.Collection?.length) return [];
    const cols = Object.keys(cashRegisterData.data.Collection[0]).map((col) => COLUMN_MAP[col] || col);
    cols.push(t('cashRegister.table.header.actions'));
    return cols;
  }, [cashRegisterData.data]);

  useEffect(() => {
    dispatch(setColumns(columns));
  }, [dispatch, columns]);

  // --- Initial setup ---
  useEffect(() => {
    dispatch(resetCashRegister());
    dispatch(setShowColumnOrder(true));
  }, [dispatch]);

  const { getCashRegisterDetails, getOperatorDetails } = useFetchDetails();

  const data = useMemo(() => {
    if (!cashRegisterData.data?.Collection?.length) return [];

    return cashRegisterData.data.Collection.map((item) => ({
      ...Object.fromEntries(Object.entries(item).map(([key, value]) => [COLUMN_MAP[key] || key, value])),
      [t('cashRegister.table.header.actions')]: (
        <div className="images_container">
          <div
            className="tooltip"
            onClick={() => {
              setShowDetails(true);
              getCashRegisterDetails(item);
              dispatch(setSelectedLocation(item.LocationName));
              dispatch(setSelectedCCY(item.Currency));
              dispatch(setSelectedItem(item));
            }}
          >
            <img src={search_file} alt="search" />
            <span className="tooltiptext">{t('cashRegister.table.tooltip.details')}</span>
          </div>
          <div className="tooltip">
            <img src={wallet} alt="wallet" />
            <span className="tooltiptext">{t('cashRegister.table.tooltip.cashRegisterPayInPayOut')}</span>
          </div>
        </div>
      ),
      ...Object.fromEntries(
        Object.entries(item).map(([key, value]) => {
          if (COLUMN_MAP[key] === t('cashRegister.table.header.device')) {
            return [
              COLUMN_MAP[key],
              value ? (
                value
              ) : (
                <span className="device_value orange">{t('cashRegister.table.cellValue.byLocation')}</span>
              ),
            ];
          }

          return [COLUMN_MAP[key], value];
        }),
      ),
    }));
  }, [cashRegisterData.data, dispatch, getCashRegisterDetails]);

  return (
    <>
      {loading && <FallbackLoader />}

      <Table
        data={data}
        dataLength={data.length}
        showFooter={false}
        className="cash_register_table"
        getCurrentChunkPosition={function (): number {
          // TODO: Adopt this for cash register
          return 1;
        }}
      />

      {showDetails && selectedOperator && operatorModalOpen ? (
        <OperatorDetails getDetails={() => getOperatorDetails(selectedOperator)} setShowDetails={setShowDetails} />
      ) : (
        showDetails &&
        selectedItem &&
        !operatorModalOpen && (
          <CashRegisterDetails
            getDetails={() => getCashRegisterDetails(selectedItem)}
            setShowDetails={setShowDetails}
          />
        )
      )}
    </>
  );
};

export default CashRegisterPage;
